import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useTenant } from '@/providers/tenant-context';

export default function TermsPage() {
  const { tenant, isWhitelabel } = useTenant();
  const serviceName = isWhitelabel && tenant?.name ? tenant.name : 'Milora';

  return (
    <div className="flex flex-col min-h-screen bg-background relative overflow-x-hidden transition-colors duration-300 font-sans">
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-background to-background pointer-events-none" />
      <div className="fixed inset-0 w-full h-full -z-10 opacity-[0.03] bg-[url('https://grainy-gradients.vercel.app/noise.svg')] pointer-events-none" />

      <header className="fixed top-0 w-full z-50 border-b border-border/10 bg-background/60 backdrop-blur-xl">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          <Link className="flex items-center gap-2 group" to="/">
            {isWhitelabel && tenant?.logoWithName ? (
              <img
                src={tenant.logoWithName}
                alt={tenant.name}
                className="h-10 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <>
                <img
                  src={isWhitelabel && tenant?.logo ? tenant.logo : '/milora-logo.png'}
                  alt={isWhitelabel ? tenant?.name : 'Milora'}
                  className="w-10 h-10 object-contain group-hover:scale-105 transition-transform duration-300"
                />
                <span className="font-bold text-2xl tracking-tighter text-foreground">
                  {isWhitelabel ? tenant?.name : 'Milora'}
                </span>
              </>
            )}
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" className="rounded-full gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 pt-32 pb-20">
        <div className="container px-6 mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-12"
          >
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-semibold text-primary uppercase tracking-wider">
                <FileText className="h-3 w-3" />
                <span>Legal</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tight text-foreground">
                Terms and Conditions
              </h1>
              <p className="text-muted-foreground text-lg">
                Last updated:{' '}
                {new Date().toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>

            <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">1. Operator</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {serviceName} is operated by{' '}
                  <a
                    href="https://simpliumtechnologies.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Simplium Technologies LLP
                  </a>
                  . These Terms and Conditions govern your use of the platform and related services
                  provided by Simplium Technologies LLP.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">2. Acceptance of Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  By accessing or using {serviceName}, creating an account, or logging in, you agree
                  to be bound by these Terms and Conditions and our{' '}
                  <Link to="/privacy" className="text-primary hover:underline">
                    Privacy Policy
                  </Link>
                  . If you do not agree, please do not use our services.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">3. Use of the Service</h2>
                <p className="text-muted-foreground leading-relaxed">
                  You agree to use {serviceName} only for lawful purposes and in accordance with
                  these terms. You are responsible for maintaining the confidentiality of your
                  account credentials and for all activity that occurs under your account.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">4. Community Conduct</h2>
                <p className="text-muted-foreground leading-relaxed">
                  When participating in communities on {serviceName}, you agree to treat other
                  members respectfully, not to post unlawful or harmful content, and to comply with
                  any additional rules set by community administrators.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">5. Content and Ownership</h2>
                <p className="text-muted-foreground leading-relaxed">
                  You retain ownership of content you submit. By posting content, you grant
                  Simplium Technologies LLP a limited license to host, display, and distribute that
                  content as needed to operate {serviceName} and the communities you join.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">6. Termination</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may suspend or terminate access to the service if you violate these terms or if
                  required by law. You may stop using the service at any time by deleting your
                  account or ceasing to log in.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">7. Disclaimer and Limitation</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {serviceName} is provided on an &quot;as is&quot; basis. To the fullest extent
                  permitted by law, Simplium Technologies LLP disclaims warranties and limits
                  liability for indirect or consequential damages arising from your use of the
                  platform.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">8. Changes to These Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Simplium Technologies LLP may update these Terms and Conditions from time to time.
                  Continued use of the service after changes are posted constitutes acceptance of
                  the updated terms.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">9. Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have questions about these Terms and Conditions, please contact Simplium
                  Technologies LLP through our{' '}
                  <Link to="/contact" className="text-primary hover:underline">
                    contact form
                  </Link>{' '}
                  or at support@milora.com.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </main>

      <footer className="py-12 px-6 border-t border-border/40 bg-muted/20">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-2">
            {isWhitelabel && tenant?.logoWithName ? (
              <img
                src={tenant.logoWithName}
                alt={tenant.name}
                className="h-8 w-auto object-contain grayscale opacity-80"
              />
            ) : (
              <>
                <img
                  src={isWhitelabel && tenant?.logo ? tenant.logo : '/milora-logo.png'}
                  alt={isWhitelabel ? tenant?.name : 'Milora'}
                  className="w-8 h-8 object-contain grayscale opacity-80"
                />
                <span className="font-bold text-lg text-muted-foreground">
                  {isWhitelabel ? tenant?.name : 'Milora'}
                </span>
              </>
            )}
          </div>
          <p className="text-sm text-muted-foreground/60">
            © {new Date().getFullYear()} Simplium Technologies LLP
          </p>
        </div>
      </footer>
    </div>
  );
}
